import type { LegalDocument, ConsentStatus, ConsentRequest, ConsentMeta } from '../types/legal';
import { localeService } from '../services/localeService';
import { toastService } from '../services/toastService';

// Type for error handling
export interface ApiError {
    message: string;
    status?: number;
}

// Request logging
function logRequest(_endpoint: string, _locale?: string) {
    // console.log(`[Legal API] Request to ${endpoint}`, locale ? `with locale: ${locale}` : 'without locale');
}

// Universal function for making API requests
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = endpoint;

    // Log request
    const localeMatch = endpoint.match(/[?&]locale=([^&]*)/);
    const locale = localeMatch ? localeMatch[1] : undefined;
    logRequest(endpoint, locale);

    // Get access token from localStorage
    const accessToken = localStorage.getItem('accessToken');

    // Set default headers
    const defaultHeaders = {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
    };

    const config: RequestInit = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
        credentials: 'include', // Include cookies for authentication
    };

    try {
        // console.log('fetch url:', url);
        const response = await fetch(url, config);

        // Handle successful response
        if (response.ok) {
            const data = await response.json();
            return data;
        }

        // Handle specific errors
        switch (response.status) {
            case 404:
                throw new Error('Document not found');
            case 409:
                throw new Error('Conflict: user has already consented to this document version');
            default:
                throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
    } catch (error) {
        // If it's a network error or other non-HTTP error
        if (error instanceof TypeError) {
            const errorMsg = 'Network error. Please check your internet connection.';
            toastService.showError(errorMsg);
            throw new Error(errorMsg);
        }

        // For all other errors show notification
        if (error instanceof Error) {
            toastService.showError(error.message);
        }

        // Re-throw error
        throw error;
    }
}

// Client for working with legal documents
export class LegalApiClient {
    /**
     * Get active version of legal document by slug
     * @param slug - document slug (e.g., 'tos', 'privacy')
     * @param locale - locale (e.g., 'en', 'ru')
     * @returns Promise with document data or null if document not found
     */
    static async getLegal(slug: string, locale?: string): Promise<LegalDocument | null> {
        try {
            // Always pass locale, even if not explicitly specified
            const actualLocale = localeService.getLocaleWithFallback(locale);
            const url = `/api/legal/${slug}?locale=${actualLocale}`;

            return await apiRequest<LegalDocument>(url);
        } catch (error) {
            // For 404 return null instead of error
            if (error instanceof Error && error.message === 'Document not found') {
                return null;
            }
            throw error;
        }
    }

    /**
     * Get user consent status for document
     * @param slug - document slug
     * @param locale - locale
     * @returns Promise with consent status
     */
    static async getConsentStatus(slug: string, locale?: string): Promise<ConsentStatus> {
        try {
            // Use locale service to determine locale with fallback
            const actualLocale = localeService.getLocaleWithFallback(locale);
            return await apiRequest<ConsentStatus>(`/api/legal/${slug}/consent-status?locale=${actualLocale}`);
        } catch (error) {
            console.error('Error fetching consent status:', error);
            // Return default value in case of error
            return {
                hasConsented: false
            };
        }
    }

    /**
     * Check if user needs to re-consent
     * @returns Promise with information about re-consent requirement
     */
    static async checkReconsentNeeded(): Promise<{ needsReconsent: boolean; documents?: string[] }> {
        try {
            return await apiRequest<{ needsReconsent: boolean; documents?: string[] }>('/api/legal/reconsent-status');
        } catch (error) {
            console.error('Error checking reconsent status:', error);
            // Return default value in case of error
            return {
                needsReconsent: false
            };
        }
    }

    /**
     * Get information about required re-consent with details
     * @returns Promise with detailed information about re-consent requirement
     */
    static async getReconsentInfo(): Promise<import('../types/legal').ReconsentStatus> {
        try {
            return await apiRequest<import('../types/legal').ReconsentStatus>('/api/legal/reconsent-info');
        } catch (error) {
            console.error('Error fetching reconsent info:', error);
            // Return default value in case of error
            return {
                needsReconsent: false
            };
        }
    }

    /**
     * Get list of documents requiring re-consent
     * @returns Promise with list of documents
     */
    static async getDocumentsNeedingReconsent(): Promise<import('../types/legal').ReconsentStatus> {
        try {
            return await apiRequest<import('../types/legal').ReconsentStatus>('/api/legal/documents-needing-reconsent');
        } catch (error) {
            console.error('Error fetching documents needing reconsent:', error);
            // Return default value in case of error
            return {
                needsReconsent: false,
                documents: []
            };
        }
    }

    /**
     * Check user consent status for specific document
     * @param slug - document slug (e.g., 'tos', 'privacy')
     * @returns Promise with consent status
     */
    static async getUserConsentStatus(slug: string): Promise<{ hasConsented: boolean }> {
        try {
            return await apiRequest<{ hasConsented: boolean }>(`/api/me/consents/${slug}/status`);
        } catch (error) {
            console.error(`Error fetching consent status for ${slug}:`, error);
            // Return default value in case of error
            return {
                hasConsented: false
            };
        }
    }

    /**
     * Get list of all user consents
     * @param slug - optional document slug for filtering
     * @returns Promise with list of consents
     */
    static async getMyConsents(slug?: string): Promise<Array<{
        id: number;
        documentSlug: string;
        documentTitle: string;
        version: number;
        acceptedAt: string;
        locale: string;
        checksum: string;
    }>> {
        try {
            const url = slug ? `/api/me/consents?slug=${slug}` : '/api/me/consents';
            return await apiRequest(url);
        } catch (error) {
            console.error('Error fetching user consents:', error);
            // Return empty array in case of error
            return [];
        }
    }

    /**
     * Submit user consent for document
     * @param slug - document slug
     * @param versionId - document version ID
     * @param meta - metadata (IP, user agent)
     * @returns Promise with consent result
     */
    static async postConsent(
        slug: string,
        versionId: number,
        meta: ConsentMeta
    ): Promise<ConsentRequest> {
        const body = {
            document_version_id: versionId,
            locale: localeService.getUserLocale(), // Use user locale
            ip: meta.ip,
            userAgent: meta.userAgent
        };

        return await apiRequest<ConsentRequest>(`/api/legal/${slug}/consents`, {
            method: 'POST',
            body: JSON.stringify(body)
        });
    }

    /**
     * Acknowledge consent with current document version
     * @param slug - document slug
     * @param locale - locale
     * @returns Promise with consent result
     */
    static async ackCurrent(slug: string, locale?: string): Promise<ConsentRequest> {
        // Use passed locale or determine automatically
        const actualLocale = localeService.getLocaleWithFallback(locale);
        return await apiRequest<ConsentRequest>(`/api/legal/${slug}/ack-current?locale=${actualLocale}`, {
            method: 'POST'
        });
    }
}

export default LegalApiClient;